import { useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { Button } from "@/components/design/Button";
import { InlineEdit } from "@/components/design/InlineEdit";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Switch } from "@/components/design/Switch";
import {
  CreateOrganizationalRecordDocument,
  CreateOrganizationalRecordDocumentDocument,
  CreateOrganizationalRecordEntryDocument,
  OrganizationalRecordAccessDocument,
  OrganizationalRecordDocument,
} from "@/graphql/generated/operations";
import { OrganizationalRecordEntryKind, OrganizationalRecordItemKind } from "@/graphql/generated/types";
import type { OrgRecordEntryDraft, OrgRecordEntryKind } from "@/lib/org-record-entry";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { personNameKey } from "@/lib/person-mentions";
import { useAgentStore } from "@/stores/agent-store";
import { useGlobalCompanySearch } from "@/lib/use-global-company-search";
import { useGlobalPersonSearch } from "@/lib/use-global-person-search";
import {
  organizationalRecordSubjectVariables,
  type OrganizationalRecordSubject,
} from "@/pages/organizational-record/organizational-record-subject";
import { userErrorMessage } from "@/lib/user-error";
import { utf8ToBase64 } from "@/lib/utf8-base64";

/** The open notebook as a file a draft can take along into the record. */
export interface NotebookFile {
  name: string;
  fileName: string;
  markdown: string;
}

const KINDS: Record<OrgRecordEntryKind, OrganizationalRecordEntryKind> = {
  NOTE: OrganizationalRecordEntryKind.Note,
  REPORT: OrganizationalRecordEntryKind.Report,
};

// Where a record opens: the subject's page, scrolled to its record.
const recordLink = (subject: OrganizationalRecordSubject) =>
  `${subject.kind === "company" ? "/c" : "/p"}/${subject.id}#organisationsakte`;

/**
 * A draft entry for the organizational record of a person or a company
 * (@external-adr GENESIS-0035-organizational-records-are-a-global-capability,
 * @external-adr GENESIS-0049-an-organisational-record-is-about-a-person-or-a-company), proposed by the assistant.
 * The user picks the person or company from search hits (the model names them, it never supplies an id), may change
 * every field and files it with their own session and rights; nothing is filed before that click. Filing is offered
 * only where the card is kept in the notebook, so what was filed stays marked there and is not filed twice.
 */
export function StudioRecordEntryCard({ draft: given, onChange: persist, notebookFile }: {
  draft: OrgRecordEntryDraft;
  onChange?: (next: OrgRecordEntryDraft) => void;
  notebookFile?: NotebookFile;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  // A working copy, as in the proposal card: edits build on each other before their write comes back.
  const givenKey = JSON.stringify(given);
  const [draft, setDraft] = useState(given);
  const [seenKey, setSeenKey] = useState(givenKey);
  if (givenKey !== seenKey) {
    setSeenKey(givenKey);
    setDraft(given);
  }
  const change = persist
    ? (patch: Partial<OrgRecordEntryDraft>) => {
        const next = { ...draft, ...patch };
        setDraft(next);
        persist(next);
      }
    : undefined;
  const [filing, setFiling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What a failed attempt already created, so a retry does not create it twice. It belongs to the subject it
  // was created for: filing for another one starts over, or the entry would land in the first one's record.
  const created = useRef<{ subject?: string; recordId?: string; documentId?: string }>({});

  const access = useQuery(OrganizationalRecordAccessDocument);
  const canWrite = access.data?.organizationalRecordAccess.canWrite === true;
  const filed = draft.filed;
  const company = draft.subject.kind === "company";
  const name = draft.subject.name;
  // The extended search's own paths (search, then the cards with avatar or logo), so a hit here is the card the
  // user knows from there. Only the subject's kind is searched.
  const personSearch = useGlobalPersonSearch({ query: name, personFilter: null, pageSize: 5, skip: company || Boolean(filed) || !name.trim() });
  const companySearch = useGlobalCompanySearch({ query: name, pageSize: 5, skip: !company || Boolean(filed) || !name.trim() });
  const hits = company
    ? companySearch.nodes.map((hit) => ({
        id: hit.id,
        name: hit.name,
        sub: null as string | null,
        avatar: { content: hit.logo?.content ?? null, mimeType: hit.logo?.mimeType ?? null },
      }))
    : personSearch.nodes.map((hit) => ({
        id: hit.id,
        name: personDisplayName(hit),
        sub: personDisplayRole(hit),
        avatar: { content: hit.avatar?.content ?? null, mimeType: hit.avatar?.mimeType ?? null },
      }));
  const searching = company ? companySearch.loading : personSearch.loading;
  // A person the user already chose under this name, where a reply asked which one they mean.
  const remembered = useAgentStore((s) => (company ? undefined : s.personChoices[personNameKey(name)]));
  // One hit is the obvious subject; with several the user chooses, with none they correct the name.
  const subjectId = draft.subjectId
    ?? (remembered && hits.some((hit) => hit.id === remembered) ? remembered : hits.length === 1 ? hits[0]!.id : undefined);
  const picked = hits.find((hit) => hit.id === subjectId);

  const file = async () => {
    if (!subjectId || !change) return;
    const subject: OrganizationalRecordSubject = { kind: draft.subject.kind, id: subjectId };
    const key = `${subject.kind}:${subject.id}`;
    if (created.current.subject !== key) created.current = { subject: key };
    setFiling(true);
    setError(null);
    try {
      let recordId = created.current.recordId;
      if (!recordId) {
        const variables = organizationalRecordSubjectVariables(subject);
        const existing = await client.query({ query: OrganizationalRecordDocument, variables, fetchPolicy: "network-only" });
        recordId = existing.data?.organizationalRecord?.id
          ?? (await client.mutate({ mutation: CreateOrganizationalRecordDocument, variables })).data!.createOrganizationalRecord.id;
        created.current.recordId = recordId;
      }
      let documentId = created.current.documentId;
      if (draft.attachNotebook && notebookFile && !documentId) {
        const out = await client.mutate({
          mutation: CreateOrganizationalRecordDocumentDocument,
          fetchPolicy: "no-cache",
          variables: {
            input: {
              recordId,
              title: notebookFile.name,
              fileName: notebookFile.fileName,
              content: { content: utf8ToBase64(notebookFile.markdown), mimeType: "text/markdown" },
            },
          },
        });
        documentId = out.data!.createOrganizationalRecordDocument.id;
        created.current.documentId = documentId;
      }
      const entry = await client.mutate({
        mutation: CreateOrganizationalRecordEntryDocument,
        fetchPolicy: "no-cache",
        variables: {
          input: {
            recordId,
            kind: KINDS[draft.kind],
            title: draft.title.trim(),
            content: draft.content.trim(),
            links: documentId ? [{ id: documentId, kind: OrganizationalRecordItemKind.Document }] : [],
          },
        },
      });
      change({
        subjectId,
        filed: { subject, name: picked?.name ?? name, entryId: entry.data!.createOrganizationalRecordEntry.id, ...(documentId ? { documentId } : {}) },
      });
    } catch (caught) {
      setError(userErrorMessage(intl, caught));
    } finally {
      setFiling(false);
    }
  };

  const editable = change && !filed;
  const label = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
  const subjectLabel = company ? label("studio.orgRecord.company", "Company") : label("studio.orgRecord.person", "Person");
  return (
    <section className="box-flat mt-2 flex flex-col gap-3 rounded-lg p-3">
      <h3 className="text-smycles-meta font-medium text-ink">
        <FormattedMessage id="studio.orgRecord.title" defaultMessage="For the organizational record" />
      </h3>
      <div className="flex flex-col gap-1.5 text-smycles-label text-ink">
        <span className="smycles-field-label">{subjectLabel}</span>
        {filed ? filed.name : (
          <>
            {editable ? (
              <InlineEdit
                value={name}
                onChange={(next) => setDraft({ ...draft, subject: { ...draft.subject, name: next } })}
                // Not while filing: the subject the entry goes to is fixed once the filing has started.
                onCommit={(next) => !filing && next.trim() && next.trim() !== given.subject.name
                  && change({ subject: { ...draft.subject, name: next.trim() }, subjectId: undefined })}
                clearable={false}
                ariaLabel={subjectLabel}
              />
            ) : name}
            {!searching && hits.length === 0 ? (
              <span className="text-ink-muted">
                <FormattedMessage
                  id="studio.orgRecord.noHit"
                  defaultMessage="{subject, select, company {No company} other {No person}} found under this name. Correct the name."
                  values={{ subject: draft.subject.kind }}
                />
              </span>
            ) : null}
            {hits.length > 1 && !subjectId ? (
              <span className="text-ink">
                <FormattedMessage
                  id="studio.orgRecord.pickCount"
                  defaultMessage="{subject, select, company {Which company?} other {Which person?}} {count} found"
                  values={{ subject: draft.subject.kind, count: hits.length }}
                />
              </span>
            ) : null}
            {/* The cards of the extended search, in its grid, the whole card the choice: whose record the entry
                goes to is seen before it is filed, and with several hits the card picks none (a list of bare
                names read as plain text, Marvin 2026-09-28). */}
            {hits.length ? (
              <ul aria-label={subjectLabel} className="grid grid-cols-1 gap-3 p-0.5 @sm:grid-cols-2">
                {hits.map((hit) => {
                  const chosen = hit.id === subjectId;
                  return (
                    <li key={hit.id}>
                      <PersonResultRow
                        id={`studio-org-record-${company ? "company" : "person"}-${hit.id}`}
                        person={{ id: hit.id, name: hit.name, avatarContent: hit.avatar.content, avatarMime: hit.avatar.mimeType }}
                        title={hit.name}
                        sub={hit.sub}
                        to={null}
                        selected={chosen}
                        onSelect={editable ? () => !chosen && !filing && change?.({ subjectId: hit.id }) : undefined}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </>
        )}
      </div>
      {/* The entry as the record will show it (OrganizationalRecordEntries: the kind small above the title,
          the text in full, then what it links), edited in place: what is seen here is what is filed. */}
      <div className="flex flex-col gap-1.5">
        <span className="smycles-field-label"><FormattedMessage id="studio.orgRecord.entry" defaultMessage="Entry" /></span>
        <article className="box-flat flex flex-col gap-2 bg-page p-4">
          {editable ? (
            <div className="self-start">
              <SegmentedControl<OrgRecordEntryKind>
                size="sm"
                value={draft.kind}
                onChange={(kind) => change({ kind })}
                options={[
                  { value: "NOTE", label: label("studio.orgRecord.kindNote", "Note") },
                  { value: "REPORT", label: label("studio.orgRecord.kindReport", "Report") },
                ]}
                aria-label={label("studio.orgRecord.kind", "Kind")}
              />
            </div>
          ) : <span className="smycles-field-meta text-ink-muted">{label(draft.kind === "REPORT" ? "studio.orgRecord.kindReport" : "studio.orgRecord.kindNote", draft.kind === "REPORT" ? "Report" : "Note")}</span>}
          {editable ? (
            <InlineEdit
              value={draft.title}
              onChange={(title) => setDraft({ ...draft, title })}
              onCommit={(title) => title.trim() && title.trim() !== given.title && change({ title: title.trim() })}
              clearable={false}
              wrap
              className="smycles-field-value font-medium"
              ariaLabel={label("studio.orgRecord.entryTitle", "Title")}
            />
          ) : <h4 className="smycles-field-value break-words font-medium">{draft.title}</h4>}
          {editable ? (
            <InlineEdit
              value={draft.content}
              multiline
              onChange={(content) => setDraft({ ...draft, content })}
              onCommit={(content) => content.trim() && content.trim() !== given.content && change({ content: content.trim() })}
              clearable={false}
              className="smycles-field-value -mx-1.5"
              ariaLabel={label("studio.orgRecord.text", "Text")}
            />
          ) : <p className="smycles-field-value whitespace-pre-wrap break-words">{draft.content}</p>}
          {notebookFile && !filed ? (
            // The notebook rides along as the entry's document, shown where the record lists what an entry links.
            <div className={`mt-1 flex items-center gap-2 border-t border-ink/10 pt-3 text-smycles-label ${draft.attachNotebook ? "text-ink" : "text-ink-muted"}`}>
              <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5 12.6 19.9a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />
              </svg>
              <span className="min-w-0 flex-1 truncate">
                <FormattedMessage
                  id="studio.orgRecord.attachFile"
                  defaultMessage="The notebook as a document: {fileName}"
                  values={{ fileName: notebookFile.fileName }}
                />
              </span>
              <Switch
                id="studio-org-record-attach"
                checked={draft.attachNotebook}
                disabled={!editable}
                onChange={(attachNotebook) => change?.({ attachNotebook })}
                ariaLabel={label("studio.orgRecord.attach", "Attach the notebook as a document")}
              />
            </div>
          ) : null}
        </article>
      </div>
      {filed ? (
        <p className="text-smycles-label text-ink">
          <FormattedMessage
            id="studio.orgRecord.filed"
            defaultMessage="Filed in the organizational record of {name}."
            values={{ name: filed.name }}
          />{" "}
          <Link to={recordLink(filed.subject)} className="underline underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui">
            <FormattedMessage id="studio.orgRecord.open" defaultMessage="Open the record" />
          </Link>
        </p>
      ) : access.data && !canWrite ? (
        <p className="text-smycles-label text-ink">
          <FormattedMessage id="studio.orgRecord.noRight" defaultMessage="You may not keep organizational records." />
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            id="studio-org-record-file"
            variant="primary"
            size="compact"
            disabled={!change || !canWrite || !subjectId || filing || !draft.title.trim() || !draft.content.trim()}
            aria-describedby={!subjectId && hits.length > 1 && editable ? "studio-org-record-why" : undefined}
            onClick={() => void file()}
          >
            <FormattedMessage id="studio.orgRecord.file" defaultMessage="File in the record" />
          </Button>
          {/* Why the button waits, where it waits: a greyed button alone does not say it. */}
          {!subjectId && hits.length > 1 && editable ? (
            <span id="studio-org-record-why" className="text-smycles-label text-ink">
              <FormattedMessage
                id="studio.orgRecord.pickFirst"
                defaultMessage="{subject, select, company {Pick the company first.} other {Pick the person first.}}"
                values={{ subject: draft.subject.kind }}
              />
            </span>
          ) : null}
          {error ? <span role="alert" className="text-smycles-label text-danger">{error}</span> : null}
        </div>
      )}
    </section>
  );
}
